import React, { FC } from 'react';
import { ApproveCrewChangeError, DeclineCrewChangeError, CancelCrewChangeError, Scalars } from '@bechtel/types';
import { useCrewChangeRequestsByIds } from '../hooks/use-crew-change';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModalState,
  Box,
  Text,
  Spinner,
  Button,
  ButtonAppearance,
} from '@bechtel/gds';
import { ApolloError } from '@apollo/client';
import { Trans, t } from '@lingui/macro';
import { CrewChangeActions } from '../types';

interface CrewChangeRequestErrorProps {
  crewChangeRequestError: ApproveCrewChangeError | DeclineCrewChangeError | CancelCrewChangeError | ApolloError;
  crewChangeRequestId: Scalars['ID'];
  crewChangeAction: CrewChangeActions | null;
}

//TODO: More specific errors needs to be added for different mutation errors.
const getErrorMessage = (error: CrewChangeRequestErrorProps['crewChangeRequestError']) => {
  if (error instanceof ApolloError)
    return (
      <Trans>
        Network error has occured.Please press the &ldquo;Retry&rdquo; button to fetch the latest data and try again.
      </Trans>
    );
  switch (error) {
    case ApproveCrewChangeError.MISSING_REQUIRED_PERMISSION:
    case DeclineCrewChangeError.MISSING_REQUIRED_PERMISSION:
    case CancelCrewChangeError.MISSING_REQUIRED_PERMISSION:
      return (
        <Trans>
          You have no required permission to approve or decline a crew change request. Please press the
          &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
    case ApproveCrewChangeError.REQUEST_IS_ALREADY_DECLINED:
    case CancelCrewChangeError.REQUEST_IS_ALREADY_DECLINED:
      return (
        <Trans>
          This crew change request has already been declined. Please press the &ldquo;Update&rdquo; button to fetch the
          latest data and try again.
        </Trans>
      );
    case DeclineCrewChangeError.REQUEST_IS_ALREADY_APPROVED:
    case CancelCrewChangeError.REQUEST_IS_ALREADY_APPROVED:
      return (
        <Trans>
          This crew change request has already been approved.. Please press the &ldquo;Update&rdquo; button to fetch the
          latest data and try again.
        </Trans>
      );
    default:
      return (
        <Trans>
          An error has occured. Please press the &ldquo;Update&rdquo; button to fetch the latest data and try again.
        </Trans>
      );
  }
};

export const CrewChangeRequestError: FC<CrewChangeRequestErrorProps> = ({
  crewChangeRequestError,
  crewChangeRequestId,
  crewChangeAction,
}) => {
  const { visible, close } = useModalState(true);
  const [loadCrewChangeRequestByIds, { loading }] = useCrewChangeRequestsByIds(true);

  return (
    <Modal visible={visible} style={{ width: '552px', height: '275px' }}>
      <ModalHeader title={t`Crew Change ${crewChangeAction ? crewChangeAction : ''} Error`}></ModalHeader>
      <ModalBody>
        <Box px={{ sm: '500' }} py={{ sm: '700' }}>
          <Text size="600">{getErrorMessage(crewChangeRequestError)}</Text>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <Button appearance={ButtonAppearance.SECONDARY} onClick={close}>
              <Trans>Close</Trans>
            </Button>
          </Box>
          <Box>
            <Button
              type="submit"
              appearance={ButtonAppearance.SECONDARY}
              onClick={async () => {
                await loadCrewChangeRequestByIds({
                  variables: {
                    ids: [crewChangeRequestId],
                  },
                });
                close();
              }}
              disabled={loading}
            >
              {loading && <Spinner></Spinner>}
              {!loading && (crewChangeRequestError instanceof ApolloError ? t`Retry` : t`Update`)}
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default CrewChangeRequestError;
